import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Minus, Plus } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import WhatsAppIcon from "./WhatsAppIcon";
import VialSvg from "./VialSvg";
import { useDrawer } from "../lib/DrawerContext";
import { useConfig, buildWhatsAppUrl } from "../lib/config";
import { api, trackWhatsApp } from "../lib/api";
import { toast } from "sonner";

const METODOS_PAGO = ["SINPE Móvil", "Transferencia bancaria"];

const EMPTY = {
  nombre: "",
  correo: "",
  telefono: "",
  direccion: "",
  metodo_pago: "SINPE Móvil",
  codigo_descuento: "",
};

const formatCRC = (n) => `₡${Math.round(n).toLocaleString("en-US")}`;

// Input class helper
const cls = (err) =>
  `w-full px-3 py-2.5 bg-[#0A0A0F] border ${
    err ? "border-[#FF3B30]" : "border-[#22222B]"
  } focus:border-[#00DF82] outline-none text-sm text-[#EDEDED] placeholder-[#5A5A66]`;

// Labeled field wrapper
const Field = ({ label, error, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-[#8F8F99] mb-1.5">{label}</div>
    {children}
    {error && <p className="text-[11px] text-[#FF3B30] mt-1">{error}</p>}
  </div>
);

const PurchaseDrawer = () => {
  const { open, product, closeDrawer } = useDrawer();
  const { config } = useConfig();
  const location = useLocation();

  const [qty, setQty]           = useState(1);
  const [form, setForm]         = useState(EMPTY);
  const [errors, setErrors]     = useState({});
  const [loading, setLoading]   = useState(false);
  const [attempted, setAttempted] = useState(false);

  // Reset when a new product is opened
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (product) {
      setQty(1);
      setForm(EMPTY);
      setErrors({});
      setAttempted(false);
    }
  }, [product?.slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const price     = product?.base_price || 0;
  const showPrice = !!(config?.show_price && price > 0);
  const subtotal  = price * qty;

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (attempted) setErrors((e) => { const n = { ...e }; delete n[k]; return n; });
  };

  const validate = () => {
    const e = {};
    if (!form.nombre.trim())  e.nombre   = "Nombre requerido";
    if (!form.telefono.trim()) e.telefono = "Teléfono requerido";
    if (!form.correo.trim())  e.correo   = "Correo requerido";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.correo)) e.correo = "Correo inválido";
    if (!form.direccion.trim()) e.direccion = "Dirección requerida";
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setAttempted(true);
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      // Step 1 — Save lead
      await api.post("/leads", {
        nombre:           form.nombre.trim(),
        correo:           form.correo.trim(),
        telefono:         form.telefono.trim(),
        direccion:        form.direccion.trim(),
        producto:         product.name,
        producto_slug:    product.slug,
        cantidad:         qty,
        precio_unitario:  price,
        subtotal,
        metodo_pago:      form.metodo_pago,
        codigo_descuento: form.codigo_descuento.trim(),
        estado:           "Nuevo",
      });

      // Step 2 — Analytics
      trackWhatsApp(location.pathname, "drawer");

      // Step 3 — Build exact WhatsApp message
      const lines = [
        "Hola, me gustaría realizar el siguiente pedido:",
        "",
        `Producto: ${product.name}`,
        `Cantidad: ${qty}`,
        `Precio unitario: ${showPrice ? formatCRC(price) : "A consultar"}`,
        `Subtotal: ${showPrice ? formatCRC(subtotal) : "A consultar"}`,
        "",
        `Nombre del cliente: ${form.nombre.trim()}`,
        `Teléfono: ${form.telefono.trim()}`,
        `Correo: ${form.correo.trim()}`,
        "",
        "Dirección:",
        form.direccion.trim(),
        "",
        "Método de pago:",
        form.metodo_pago,
        "",
        "Código de descuento:",
        form.codigo_descuento.trim() || "No aplica",
        "",
        "Quedo atento(a) a la confirmación del pedido. Muchas gracias.",
      ];

      const waUrl = buildWhatsAppUrl(config.whatsapp_number, lines.join("\n"));
      window.open(waUrl, "_blank", "noopener,noreferrer");

      toast.success("Pedido registrado. Abriendo WhatsApp…");
      closeDrawer();
    } catch (err) {
      console.error("[PurchaseDrawer]", err);
      toast.error("Error al registrar el pedido. Intentá de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const accent = product?.availability === "Próximamente" ? "#00A3FF" : "#00DF82";

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) closeDrawer(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg bg-[#12121A] border-l border-[#22222B] p-0 overflow-y-auto [&>button]:text-[#8F8F99] [&>button:hover]:text-white"
        data-testid="purchase-drawer"
      >
        <SheetTitle className="sr-only">Consultar producto</SheetTitle>
        {product && (
          <form onSubmit={handleSubmit} noValidate>

            {/* ── Header ── */}
            <div className="p-6 border-b border-[#22222B] flex items-start gap-4 pt-10">
              <div
                className="w-16 h-16 shrink-0 border border-[#22222B] overflow-hidden"
                style={{ background: "radial-gradient(circle at 50% 60%, #0F1A24 0%, #08080C 70%)" }}
              >
                {product.image_url ? (
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <VialSvg
                    seed={product.name.length % 17 + 1}
                    color={accent}
                    label={product.name}
                    className="w-full h-full"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-[#8F8F99] mb-1">
                  {product.category}
                </div>
                <div className="font-display text-xl leading-tight text-white">{product.name}</div>
                <div className="text-xs text-[#8F8F99] mt-0.5">{product.scientific_name}</div>
                {showPrice && (
                  <div className="text-lg font-medium mt-2" style={{ color: accent }}>
                    {formatCRC(price)}
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 space-y-5">

              {/* ── Quantity ── */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#8F8F99] mb-2">Cantidad</div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    data-testid="drawer-qty-minus"
                    className="w-9 h-9 border border-[#22222B] flex items-center justify-center hover:border-[#00DF82] hover:text-[#00DF82] transition-colors"
                  >
                    <Minus size={14} />
                  </button>
                  <span
                    data-testid="drawer-qty"
                    className="text-2xl font-display w-10 text-center select-none"
                  >
                    {qty}
                  </span>
                  <button
                    type="button"
                    onClick={() => setQty((q) => q + 1)}
                    data-testid="drawer-qty-plus"
                    className="w-9 h-9 border border-[#22222B] flex items-center justify-center hover:border-[#00DF82] hover:text-[#00DF82] transition-colors"
                  >
                    <Plus size={14} />
                  </button>
                  {showPrice && (
                    <div className="ml-3 text-sm text-[#8F8F99]">
                      Subtotal:{" "}
                      <span
                        data-testid="drawer-subtotal"
                        className="font-medium"
                        style={{ color: accent }}
                      >
                        {formatCRC(subtotal)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Required fields ── */}
              <Field label="Nombre completo *" error={errors.nombre}>
                <input
                  data-testid="drawer-nombre"
                  value={form.nombre}
                  onChange={(e) => setField("nombre", e.target.value)}
                  placeholder="Tu nombre completo"
                  className={cls(errors.nombre)}
                />
              </Field>

              <Field label="Teléfono *" error={errors.telefono}>
                <input
                  data-testid="drawer-telefono"
                  type="tel"
                  value={form.telefono}
                  onChange={(e) => setField("telefono", e.target.value)}
                  placeholder="Ej: 8888-8888"
                  className={cls(errors.telefono)}
                />
              </Field>

              <Field label="Correo electrónico *" error={errors.correo}>
                <input
                  data-testid="drawer-correo"
                  type="email"
                  value={form.correo}
                  onChange={(e) => setField("correo", e.target.value)}
                  placeholder="tu@correo.com"
                  className={cls(errors.correo)}
                />
              </Field>

              <Field label="Dirección exacta de entrega *" error={errors.direccion}>
                <textarea
                  data-testid="drawer-direccion"
                  rows={3}
                  value={form.direccion}
                  onChange={(e) => setField("direccion", e.target.value)}
                  placeholder="Provincia, cantón, distrito, señas exactas…"
                  className={cls(errors.direccion)}
                />
              </Field>

              {/* ── Método de pago ── */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#8F8F99] mb-2">
                  Método de pago *
                </div>
                <div className="space-y-2">
                  {METODOS_PAGO.map((m) => (
                    <label
                      key={m}
                      data-testid={`drawer-pago-${m.toLowerCase().replace(/\s+/g, "-")}`}
                      className="flex items-center gap-3 cursor-pointer group"
                    >
                      <input
                        type="radio"
                        name="metodo_pago"
                        value={m}
                        checked={form.metodo_pago === m}
                        onChange={() => setField("metodo_pago", m)}
                        className="accent-[#00DF82] w-4 h-4"
                      />
                      <span className="text-sm text-[#EDEDED] group-hover:text-white transition-colors">
                        {m}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* ── Optional discount ── */}
              <Field label="Código de descuento (opcional)">
                <input
                  data-testid="drawer-descuento"
                  value={form.codigo_descuento}
                  onChange={(e) => setField("codigo_descuento", e.target.value)}
                  placeholder="CÓDIGO123"
                  className={cls(false)}
                />
              </Field>

              {/* ── Submit ── */}
              <button
                type="submit"
                data-testid="drawer-submit-btn"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 text-xs uppercase tracking-[0.2em] bg-[#00DF82] text-black hover:bg-white transition-colors disabled:opacity-60 mt-2"
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <WhatsAppIcon size={16} />
                )}
                Completar pedido por WhatsApp
              </button>

              <p className="text-[10px] text-[#5A5A66] text-center">
                Tu información se enviará al equipo de Péptidos Costa Rica.
              </p>
            </div>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default PurchaseDrawer;
